import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Select, Tooltip } from '@xsolla/ui-core';

import { getUserGroups } from '../../../api';
import { IGetParams } from '../../../hooks/types';
import { SelectValue } from './style';
import { Groups, SelectedGroups } from './types';

type Props = {
  otherParams: IGetParams;
  setParams: React.Dispatch<React.SetStateAction<IGetParams>>;
  projectId: string;
};

export const SelectGroups = ({ otherParams, setParams, projectId }: Props): JSX.Element => {
  const intl = useIntl();
  const [selectedGroups, setSelectedGroups] = useState<SelectedGroups>({});
  const [groups, setGroups] = useState<Groups>({});
  const [groupsError, setGroupsError] = useState<boolean>(false);

  const setNewStatus = (params: IGetParams) => {
    setParams((prev) => ({
      ...prev,
      user_groups: params.user_groups
    }));
  };

  const getUserGroupsHandler = async () => {
    try {
      const response = await getUserGroups(projectId);
      setGroups(
        response.reduce((acc, item) => {
          return item.is_default
            ? acc
            : {
                ...acc,
                [item.id]: item
              };
        }, {})
      );
    } catch (err) {
      setGroupsError(true);
    }
  };

  useEffect(() => {
    getUserGroupsHandler();
  }, []);

  useEffect(() => {
    const preparedQueryString = Object.entries(selectedGroups).reduce((acc, [key, value]) => {
      if (!value) {
        return acc;
      }
      return `${acc}user_groups=${key}&`;
    }, '');

    if (preparedQueryString !== otherParams.user_groups) {
      setNewStatus({ user_groups: preparedQueryString });
    }
  }, [selectedGroups]);

  const selectUserGroups = useMemo(() => {
    return Object.values(groups).reduce(
      (acc, item) => [
        ...acc,
        {
          children: item?.name,
          value: item?.id,
          palette: selectedGroups[item.id] ? 'brand' : null,
          'data-test-id': `select-group-${item?.id}`
        }
      ],
      []
    );
  }, [groups, selectedGroups]);

  const selectGroupHandle = (value: number) => {
    setSelectedGroups((prev) => ({
      ...prev,
      [value]: prev[value] ? null : groups[value].name
    }));
  };

  const currentValues = useMemo(
    () =>
      Object.values(selectedGroups)
        .filter((i) => i)
        .reduce((acc, item, index): string => {
          if (!index) {
            return item?.toString() || '';
          }
          return `${acc}, ${item}`;
        }, ''),
    [selectedGroups]
  );

  if (groupsError) {
    return (
      <Tooltip content={intl.formatMessage({ id: 'app.error.user-groups.tooltip' })}>
        <div>
          <Select
            options={selectUserGroups}
            placeholder={intl.formatMessage({
              id: 'users.list-block.select-group.select.placeholder'
            })}
            onChange={selectGroupHandle}
            size="sm"
            renderValue={({ placeholder }) => {
              return currentValues ? <SelectValue>{currentValues}</SelectValue> : placeholder;
            }}
            disabled={groupsError}
            data-test-id="select-groups"
          />
        </div>
      </Tooltip>
    );
  }

  return (
    <Select
      options={selectUserGroups}
      placeholder={intl.formatMessage({
        id: 'users.list-block.select-group.select.placeholder'
      })}
      onChange={selectGroupHandle}
      size="sm"
      renderValue={({ placeholder }) => {
        return currentValues ? <SelectValue>{currentValues}</SelectValue> : placeholder;
      }}
      disabled={!selectUserGroups.length || groupsError}
      data-test-id="select-groups"
    />
  );
};
